import type { TaskResponseDTO, TaskSortBy } from '@relay-towing/api';
import {
  Alert,
  BottomSheet,
  BottomSheetContent,
  Button,
  KeyValueRow,
  Loading,
  RecordRow,
  Stack,
  StatusChip,
  Text,
  toneForStatus,
  sentenceCase,
} from '@relay-towing/ui';
import { useNavigate, useParams } from 'react-router-dom';
import { usePagedList } from '../../hooks/usePagedList';
import { useCompleteTask, useTask } from '../../hooks/useTask';

const day = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString() : undefined;

/** One task, opened from the list or a push's /tasks/:id, and marked done. */
function TaskSheet({ taskId }: { taskId: string }) {
  const navigate = useNavigate();
  const { data: task, isLoading, isError } = useTask(taskId);
  const complete = useCompleteTask();
  const close = () => navigate('/', { replace: true });

  return (
    <BottomSheet open onOpenChange={(open) => !open && close()}>
      <BottomSheetContent
        eyebrow="Task"
        title={task?.taskTitle ?? 'Task'}
        footer={
          task && task.status !== 'COMPLETED' ? (
            <Button
              size="xl"
              pill
              loading={complete.isPending}
              onClick={() =>
                complete.mutate(task.id, { onSuccess: () => close() })
              }
            >
              Mark done
            </Button>
          ) : undefined
        }
      >
        {isLoading ? <Loading label="Loading the task" /> : null}
        {isError ? (
          <Alert tone="destructive">
            This task is not available. It may be done, or not yours to see.
          </Alert>
        ) : null}
        {task ? (
          <Stack gap={3}>
            {task.description ? <Text>{task.description}</Text> : null}
            <Stack gap={0} divided>
              <KeyValueRow
                label="Status"
                value={
                  <StatusChip
                    label={sentenceCase(task.status)}
                    tone={toneForStatus(task.status)}
                  />
                }
              />
              <KeyValueRow label="Due" value={day(task.dueDate)} hideEmpty />
              <KeyValueRow label="For" value={task.driver?.name} hideEmpty />
            </Stack>
          </Stack>
        ) : null}
        {complete.isError ? (
          <Alert tone="destructive">The task could not be marked done.</Alert>
        ) : null}
      </BottomSheetContent>
    </BottomSheet>
  );
}

/**
 * Home's tasks: a driver's own, or everyone's for a role with
 * `tasks.view_all`, as the server scopes GET /tasks. Open ones first, by
 * status on the server. /tasks/:id opens one over Home.
 */
export function TasksSection() {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const list = usePagedList<TaskResponseDTO>('mobile-tasks', '/tasks', {
    sortBy: 'status' satisfies TaskSortBy,
    sortOrder: 'desc',
  });

  return (
    <Stack gap={2} as="section">
      <Text variant="overline" tone="muted" as="h2">
        Tasks
      </Text>
      {list.isLoading ? <Loading label="Loading tasks" /> : null}
      {!list.isLoading && list.items.length === 0 ? (
        <Text variant="body-sm" tone="muted">
          No tasks.
        </Text>
      ) : null}
      {list.items.length ? (
        <Stack gap={0} divided>
          {list.items.map((task) => (
            <RecordRow
              key={task.id}
              title={task.taskTitle}
              meta={[task.driver?.name, day(task.dueDate)]
                .filter(Boolean)
                .join(' · ')}
              status={
                <StatusChip
                  label={sentenceCase(task.status)}
                  tone={toneForStatus(task.status)}
                />
              }
              // Flush with the call above it, as the calls list is: the
              // compact density's 12px side inset set the tasks in.
              onClick={() => navigate(`/tasks/${task.id}`)}
            />
          ))}
        </Stack>
      ) : null}
      {list.hasMore ? (
        <Button variant="outline" onClick={list.loadMore}>
          Show more tasks
        </Button>
      ) : null}
      {id ? <TaskSheet taskId={id} /> : null}
    </Stack>
  );
}
